// Displays exam info, candidate info, and timer
import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Stack,
  Chip,
  Container,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Quiz as QuizIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import { useExam } from './ExamContext';
import Timer from './Timer';

const ExamHeader = ({ onClose }) => {
  const { state } = useExam();
  const { examInfo, candidate } = state;
  
  // Enhanced full-screen logic with better browser support
  const goFullScreen = () => {
    const element = document.documentElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  };
  
  return (
    <AppBar 
      position="sticky" 
      elevation={1}
      sx={{ 
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ minHeight: { xs: 64, md: 80 }, px: 0, pr: { xs: 10, md: 12 } }}>
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', md: 'center' }}
            width="100%"
            spacing={{ xs: 1, md: 2 }}
          >
            {/* Left Section - Exam Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <QuizIcon color="primary" />
                <Typography 
                  variant="h6" 
                  component="h1"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: 'primary.main'
                  }}
                  noWrap
                >
                  {examInfo?.title || 'Exam'}
                </Typography>
              </Stack>
              
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {examInfo?.year && (
                  <Chip 
                    label={`Year: ${examInfo.year}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                {examInfo?.subject && (
                  <Chip 
                    label={`Subject: ${examInfo.subject}`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            </Box>

            {/* Center Section - Timer */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: { xs: 'auto', md: 200 }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ScheduleIcon color="warning" />
                <Timer />
              </Stack>
            </Box>

            {/* Right Section - Candidate Info & Action Buttons */}
            <Box 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: 'center',
                minWidth: 0,
                gap: 2
              }}
            >
              {/* Candidate Info */}
              {candidate?.name && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="action" />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                    noWrap
                  >
                    {candidate.name}
                  </Typography>
                </Stack>
              )}
              
              {/* Action Buttons */}
              <Stack direction="row" spacing={1}>
                {/* Fullscreen Button */}
                <Tooltip title="Fullscreen Mode">
                  <IconButton
                    onClick={goFullScreen}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.2s ease',
                      boxShadow: 2
                    }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {/* Close Exam Button */}
                {onClose && (
                  <Tooltip title="Close Exam">
                    <IconButton
                      onClick={onClose}
                      size="small"
                      sx={{
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'error.dark',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease',
                        boxShadow: 2
                      }}
                    >
                      <ExitToAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ExamHeader;
